/**
 * Analytics (PostHog) — EU Cloud only, loads only after explicit opt-in.
 *
 * Build / Coolify (Dockerfile ARG → CRA bake-in):
 *   REACT_APP_POSTHOG_HOST=https://eu.i.posthog.com   (default)
 *   REACT_APP_POSTHOG_KEY=phc_…                       (EU project API key)
 *
 * Create the EU project at https://eu.posthog.com (US keys do not work on EU).
 * Session recording stays off.
 *
 * Consent storage is versioned: bump CONSENT_VERSION to re-prompt everyone
 * (e.g. after policy changes). Default is unset → banner → no PostHog until accept.
 */

const CONSENT_KEY = 'mamv_analytics_consent';
/** Bump to invalidate prior decisions and show the banner again. */
const CONSENT_VERSION = 2;
const DEFAULT_HOST = 'https://eu.i.posthog.com';

let initStarted = false;

/**
 * @returns {'granted'|'denied'|null}
 * null = not decided (or outdated version) → show banner, do not load PostHog
 */
export function getAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    // Pre-versioning values — treat as undecided so opt-in is asked again
    if (raw === 'granted' || raw === 'denied') {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    if (parsed.status === 'granted' || parsed.status === 'denied') return parsed.status;
    return null;
  } catch {
    return null;
  }
}

export function setAnalyticsConsent(value) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ v: CONSENT_VERSION, status: value }),
    );
  } catch {
    /* ignore quota / private mode */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('analytics-consent-changed', { detail: value }));
  }
}

function posthogKey() {
  return (process.env.REACT_APP_POSTHOG_KEY || '').trim();
}

function posthogHost() {
  return (process.env.REACT_APP_POSTHOG_HOST || DEFAULT_HOST).trim() || DEFAULT_HOST;
}

/** Remove PostHog localStorage + cookies (incl. leftover keys from old projects). */
export function clearPosthogStorage() {
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      if (
        k.startsWith('ph_')
        || k.startsWith('phc_')
        || k.startsWith('posthog')
        || k.includes('posthog')
      ) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.split('=')[0].trim();
      if (name.startsWith('ph_') || name.includes('posthog')) {
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}; SameSite=Lax`;
      }
    });
  } catch {
    /* ignore */
  }
}

/** Install PostHog stub + init — only called after explicit grant. */
function ensurePosthogStub() {
  if (window.posthog?.__SV || window.posthog?.init) return;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1]));
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src =
            s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') +
            '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            (o =
              'init capture identify reset opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing get_distinct_id'.split(
                ' ',
              )),
            (n = 0);
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */
}

/** Load snippet + init only once, and only when consent === granted. */
export function initAnalyticsIfConsented() {
  if (initStarted) return;
  if (getAnalyticsConsent() !== 'granted') return;
  if (typeof window === 'undefined') return;

  const key = posthogKey();
  if (!key) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[analytics] REACT_APP_POSTHOG_KEY fehlt — Tracking deaktiviert (EU-Projekt anlegen).');
    }
    return;
  }

  initStarted = true;
  ensurePosthogStub();
  try {
    window.posthog.init(key, {
      api_host: posthogHost(),
      ui_host: 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      persistence: 'localStorage+cookie',
      disable_session_recording: true,
      capture_pageview: true,
      respect_dnt: true,
    });
  } catch (e) {
    console.warn('PostHog init failed', e);
  }
}

/** Call on boot when consent is missing/denied — wipe leftover PostHog state. */
export function ensureAnalyticsIdle() {
  if (getAnalyticsConsent() === 'granted') return;
  clearPosthogStorage();
  try {
    if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing();
  } catch {
    /* ignore */
  }
}

export function optOutAnalytics() {
  setAnalyticsConsent('denied');
  initStarted = false;
  try {
    if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing();
  } catch {
    /* ignore */
  }
  clearPosthogStorage();
}

export function optInAnalytics() {
  setAnalyticsConsent('granted');
  initAnalyticsIfConsented();
}
